// src/components/padrekko/qc/QcPolygonDrawer.jsx
//
// Panel-mounted polygon drawer for Reclamation QC.
//
// Mount location: inside QcInspector, inside the QC tab of PadRekkoPanel.
// Reads map + draw via useAppViewStore rather than props. Rationale:
//   - Tab-scoped lifecycle: when the operator switches to Volumetrics
//     (or any other tab), the inspector unmounts → this component
//     unmounts → our useEffect cleanup tears down ml-draw + flips
//     drawingPolygon=false. That auto-cleanup is the whole reason to
//     panel-mount this rather than mount it as a MapView child like
//     PadRoadDrawer.
//   - Avoids prop-drilling map + draw four levels through PadRekkoPanel
//     → ReclamationQcTab → QcInspector → here.
// Sibling MapView-level components (PadLayer, PadRoadDrawer, etc.)
// receive map/draw as props from MapView because their lifecycle is
// MapView's lifecycle. Ours is the QC tab's lifecycle — different
// scope, different plumbing. See useAppViewStore comments.
//
// Behavior:
//   - "Draw polygon" button starts a polygon draw using the shared MlDraw
//     instance (sourced from useAppViewStore). Operator clicks vertices,
//     right-clicks to commit. (Right-click → completePolygon is wired
//     globally in MapView.)
//   - On commit, we run a careful four-step snapshot:
//       1. mlDraw.delete() + mlDraw.stopDrawing() — clear the polygon
//          fill/line so it doesn't appear in the image AI sees.
//          Polygon is communicated via WKT only.
//       2. await map.once("idle") — wait for MapLibre to finish painting
//          the cleared frame. getCanvas().toDataURL reads whatever's on
//          the GL context RIGHT NOW; without this wait we'd risk a stale
//          frame where the polygon was still drawn.
//       3. snapshot via getCanvas().toDataURL("image/jpeg", 0.85).
//       4. dispatch analyzeFinding with polygonWkt + polygonGeoJson + b64.
//     During steps 1-3, drawingPolygon stays true so QcFindingsLayer also
//     hides itself — clean imagery for AI, no residual rendering from
//     prior findings either.
//   - Esc cancels mid-draw. The keydown listener is installed when
//     drawingPolygon flips true and removed when it flips false.
//   - Defensive teardown on unmount: if drawer disappears mid-draw (tab
//     switch, panel close, session clear), tear down MlDraw and reset
//     drawingPolygon. ml-draw doesn't fire a cancel callback in every
//     case, so we don't rely on it.
//
// What this component does NOT do:
//   - Render finding cards. That's QcFindingCard, called from
//     QcInspector after analyzeFinding lands a finding in the store.
//   - Render the polygon outline as a layer for navigation/zoom. That's
//     QcFindingsLayer, which subscribes to session.pads[i].findings.

import { useCallback, useEffect, useRef } from "react";
import { Pencil, Loader2, X } from "lucide-react";
import { useAppViewStore } from "../../../stores/useAppViewStore";
import GatedAction from "./GatedAction";
import { useQcStore } from "../../../stores/useQcStore";

// GeoJSON ring (array of [lng, lat]) → OGC WKT POLYGON string suitable for
// PostGIS ST_GeomFromText. We send WKT (not GeoJSON) because the analyze
// endpoint expects `polygons: [{idx, wkt}]`.
function ringToWkt(ring) {
  const pairs = ring.map(([x, y]) => `${x} ${y}`).join(", ");
  return `POLYGON((${pairs}))`;
}

// data:image/jpeg;base64,XXXX → XXXX. The API wants raw base64, no prefix.
function stripDataUrl(dataUrl) {
  const comma = dataUrl.indexOf(",");
  return comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl;
}

export default function QcPolygonDrawer() {
  const mapInstance         = useAppViewStore((s) => s.mapInstance);
  const drawInstance        = useAppViewStore((s) => s.drawInstance);
  const session             = useQcStore((s) => s.session);
  const currentPadIdx       = useQcStore((s) => s.currentPadIdx);
  const drawingPolygon      = useQcStore((s) => s.drawingPolygon);
  const analyzeInflightPadId = useQcStore((s) => s.analyzeInflightPadId);
  const setDrawingPolygon   = useQcStore((s) => s.setDrawingPolygon);
  const analyzeFinding      = useQcStore((s) => s.analyzeFinding);
  // Read-only gate (matches QcFindingCard's pattern). Blocks the entry
  // point only — once a draw starts, the user can complete or cancel
  // without re-checks (mid-commit rejection would be worse UX than the
  // backend 403 from a stale-state edge case).
  const caseWriteable       = useQcStore((s) => s.caseWriteable);
  const readOnlyTitle       = "Read-only at this stage — only the current stage owner can modify findings";

  // Day 8 Phase 2 — polygon edit. When set for a finding on the current
  // pad, this component loads the polygon into MlDraw and enters vertex-
  // edit. The Submit/Cancel buttons live inside QcFindingCard's correct
  // mode (δ1 — keep correct mode inline); this component just shows a
  // status row and hides its "Draw polygon to add" button so the draw
  // affordance doesn't compete with edit mode.
  const polygonEditMode = useQcStore((s) => s.polygonEditMode);

  // Track the current draw cycle's previously-set onCreateCallback so we
  // restore it if cancelled. AoiToolbar etc. may have it set; we shouldn't
  // permanently clobber it just because the QC tab was open.
  const prevOnCreateRef = useRef(null);
  // Guards a stale-callback race: if the operator hits Esc just as
  // completePolygon resolves, we want the shim to no-op.
  const cycleActiveRef = useRef(false);

  const pad = session?.pads?.[currentPadIdx ?? 0];
  const padIdx = currentPadIdx ?? 0;

  const isAnalyzingThisPad = pad && analyzeInflightPadId === pad.id;
  const isEditingOnThisPad =
    !!polygonEditMode && polygonEditMode.padIdx === padIdx;
  const canDraw =
    !!mapInstance && !!drawInstance && !!pad && !drawingPolygon && !isAnalyzingThisPad && caseWriteable && !isEditingOnThisPad;

  // Tear down ml-draw + restore prior onCreate. Idempotent — safe to call
  // multiple times. Cursor reset is handled via the .qc-drawing body
  // class toggle below (CSS path), not inline style — see useEffect.
  const teardown = useCallback(() => {
    cycleActiveRef.current = false;
    if (drawInstance) {
      try { drawInstance.stopDrawing?.(); } catch { /* noop */ }
      try { drawInstance.delete?.();      } catch { /* noop */ }
      drawInstance.onCreateCallback = prevOnCreateRef.current;
    }
    prevOnCreateRef.current = null;
  }, [drawInstance]);

  const cancelDraw = useCallback(() => {
    teardown();
    setDrawingPolygon(false);
  }, [teardown, setDrawingPolygon]);

  // The full commit pipeline: clear → wait for idle → snapshot → dispatch.
  // Pulled out so the onCreate shim can call it without closing over stale
  // state.
  const handleCommit = useCallback(
    async (feature) => {
      if (!feature?.geometry?.coordinates?.[0]) {
        // Defensive — shouldn't happen, completePolygon returns null on
        // too-few points and we never invoke it manually.
        teardown();
        setDrawingPolygon(false);
        return;
      }

      // 1. Clear ml-draw's final polygon + handles so the snapshot is
      //    clean. drawingPolygon stays true → QcFindingsLayer also hidden.
      try { drawInstance.delete?.();      } catch { /* noop */ }
      try { drawInstance.stopDrawing?.(); } catch { /* noop */ }
      drawInstance.onCreateCallback = prevOnCreateRef.current;
      prevOnCreateRef.current = null;
      cycleActiveRef.current = false;

      // 2. Wait for MapLibre to repaint the cleared frame. getCanvas()
      //    reflects whatever the GL context just painted; without this
      //    we'd risk snapshotting a frame still containing the polygon.
      await new Promise((resolve) => {
        if (!mapInstance) return resolve();
        mapInstance.once("idle", resolve);
      });

      // 3. Snapshot — JPEG quality 0.85 matches the Don plugin's
      //    mapserver tile fetch.
      let imageB64;
      try {
        const dataUrl = mapInstance.getCanvas().toDataURL("image/jpeg", 0.85);
        imageB64 = stripDataUrl(dataUrl);
      } catch (err) {
        console.error("[QcPolygonDrawer] canvas snapshot failed", err);
        setDrawingPolygon(false);
        return;
      }

      // 4. Dispatch. Findings layer becomes visible again on the flag flip;
      //    the new finding will appear on it once analyzeFinding resolves.
      const polygonGeoJson = feature.geometry;
      const polygonWkt = ringToWkt(polygonGeoJson.coordinates[0]);

      setDrawingPolygon(false);
      await analyzeFinding({
        padIdx,
        polygonWkt,
        polygonGeoJson,
        imageB64,
        mediaType: "image/jpeg",
      });
    },
    [drawInstance, mapInstance, padIdx, analyzeFinding, setDrawingPolygon, teardown]
  );

  function startDraw() {
    if (!canDraw) return;

    // Install one-shot onCreate shim. MlDraw._commit fires this synchronously
    // when completePolygon resolves (which the global right-click handler in
    // MapView triggers). We save the previous handler and restore in commit
    // or cancel.
    prevOnCreateRef.current = drawInstance.onCreateCallback ?? null;
    cycleActiveRef.current = true;
    drawInstance.onCreateCallback = (feature) => {
      if (!cycleActiveRef.current) return;
      handleCommit(feature);
    };

    setDrawingPolygon(true);
    try {
      drawInstance.startPolygon();
      // Cursor → crosshair handled by the .qc-drawing body class
      // toggle in useEffect below; CSS path wins over MapLibre's
      // continuous inline-style rewrites.
    } catch (err) {
      console.error("[QcPolygonDrawer] startPolygon failed", err);
      teardown();
      setDrawingPolygon(false);
    }
  }

  // Esc cancellation. Only installed while drawing.
  useEffect(() => {
    if (!drawingPolygon) return;
    function onKey(e) {
      if (e.key === "Escape") {
        e.preventDefault();
        cancelDraw();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [drawingPolygon, cancelDraw]);

  // Body-class toggle for the draw cursor. CSS rule lives in
  // styles/maplibre-draw.css (.qc-drawing .maplibregl-canvas { cursor:
  // crosshair !important }). The body class is the simplest reliable
  // path because MapLibre rewrites canvas.style.cursor on every
  // mousemove — inline-style sets get stomped within a frame; a
  // class-scoped !important rule wins for as long as the class is on.
  useEffect(() => {
    if (!drawingPolygon) return;
    document.body.classList.add("qc-drawing");
    return () => document.body.classList.remove("qc-drawing");
  }, [drawingPolygon]);

  // Unmount safety net. If the drawer disappears mid-draw (tab switch,
  // session clear, panel collapse), tear ml-draw down so the operator
  // doesn't return to a stuck crosshair + flag.
  useEffect(() => {
    return () => {
      const stillDrawing = useQcStore.getState().drawingPolygon;
      if (stillDrawing) {
        teardown();
        useQcStore.getState().setDrawingPolygon(false);
      }
    };
    // teardown closes over drawInstance which is stable for a session;
    // we deliberately don't re-run on its change to keep this a true
    // unmount-only hook.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Day 8 Phase 2 — polygon edit lifecycle. On polygonEditMode entry,
  // load the existing polygon into MlDraw as the active feature + flip
  // into vertex-edit. On exit (cancel or successful submit), tear MlDraw
  // down. Cleanup runs on unmount too (defense in depth for tab switch
  // mid-edit). Note: padIdx scoping is enforced via isEditingOnThisPad
  // so a stale edit mode on a different pad doesn't load here.
  useEffect(() => {
    if (!drawInstance) return;
    if (!isEditingOnThisPad) return;
    if (!polygonEditMode?.originalPolygon) return;

    const feature = {
      type: "Feature",
      geometry: polygonEditMode.originalPolygon,
      properties: {},
    };
    try {
      drawInstance.add(feature);
      drawInstance.enterVertexEdit();
    } catch (err) {
      console.error("[QcPolygonDrawer] enter vertex-edit failed", err);
    }

    return () => {
      try {
        drawInstance.exitVertexEdit?.();
        drawInstance.delete?.();
      } catch (err) {
        console.error("[QcPolygonDrawer] exit vertex-edit cleanup", err);
      }
    };
  }, [drawInstance, isEditingOnThisPad, polygonEditMode?.findingId, polygonEditMode?.originalPolygon]);

  // ─── render ──────────────────────────────────────────────────────────────

  if (!pad) {
    return null;
  }

  // While analyzing this pad, show a non-interactive status row instead of
  // the button — keeps the operator from queueing a second draw on top of
  // an inflight analyze.
  if (isAnalyzingThisPad) {
    return (
      <div className="flex items-center gap-2 text-[11px] text-amber-300/90 py-1">
        <Loader2 size={12} className="animate-spin" />
        Analyzing polygon…
      </div>
    );
  }

  if (drawingPolygon) {
    return (
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center gap-2 text-[11px] text-amber-300/90">
          <Pencil size={12} />
          Click vertices on the map · right-click to finish
        </div>
        <button
          type="button"
          onClick={cancelDraw}
          className="flex items-center gap-1.5 text-[10px] px-2 py-1 rounded
                     border border-slate-700/60 bg-slate-800/40 text-slate-400
                     hover:bg-slate-800 hover:text-slate-200 transition self-start"
          title="Cancel draw (Esc)"
        >
          <X size={10} />
          Cancel · Esc
        </button>
      </div>
    );
  }

  // Day 8 Phase 2 — polygon edit status. Cancel/Submit live in the card's
  // correct mode (δ1). This row just orients the user to what's happening
  // on the map.
  if (isEditingOnThisPad) {
    return (
      <div className="flex items-center gap-2 text-[11px] text-amber-300/90 py-1">
        <Pencil size={12} />
        Editing polygon · drag vertices · click edge to add · right-click to remove
      </div>
    );
  }

  // Second consumer of GatedAction. Behaviour is identical to before — the
  // same conditions disable the same button with the same words — but the
  // reason now lives on a wrapper that is not itself disabled, so it is
  // actually reachable on hover. Previously the title sat on the disabled
  // <button>, where no browser fires the events that would show it: the
  // explanation existed in the source and never on screen.
  //
  // canDraw folds seven conditions; only two have anything to tell the user,
  // and the rest are transient states where silence is correct.
  const drawReason =
    !caseWriteable
      ? readOnlyTitle
      : !drawInstance
      ? "Map not ready"
      : null;

  return (
    <GatedAction enabled={canDraw} reason={drawReason}>
      <button
        type="button"
        onClick={startDraw}
        title={canDraw ? "Click to draw a polygon around a feature of interest" : undefined}
        className={`flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded border transition
                    ${canDraw
                      ? "bg-amber-500/15 border-amber-500/40 text-amber-200 hover:bg-amber-500/25"
                      : "bg-slate-800/30 border-slate-800/60 text-slate-600 cursor-not-allowed"}`}
      >
        <Pencil size={11} />
        Draw polygon to add
      </button>
    </GatedAction>
  );
}
